import React from 'react';

const NAVY = '#0076A3';
const GOLD = '#00A0E0';
const GREEN = '#059669';
const GREY = '#CBD5E1';
const GREY_TXT = '#94A3B8';

const LABELS = {
  applied: 'Applied', screening: 'Screening', assessment: 'Assessment',
  interview: 'Interview', bgv: 'BGV', credentials: 'Credentials',
  offer: 'Offer', hired: 'Hired',
};

export function fmtDays(d) {
  if (d === null || d === undefined) return '—';
  if (d < 0) d = 0;
  if (d < 1 / 24) return '<1h';
  if (d < 1) return `${Math.round(d * 24)}h`;
  const r = Math.round(d * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}d`;
}

const stepColor = (status) => (status === 'done' ? NAVY : status === 'current' ? GOLD : GREY);

// Full horizontal stepped journey for the Candidate Detail page.
export function CandidateJourney({ journey }) {
  if (!journey || !journey.stages) return null;
  const { stages, is_terminal, terminal_stage, total_days, current_stage } = journey;
  const today = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const currentStep = stages.find((s) => s.status === 'current');

  return (
    <div data-testid="candidate-journey" style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: NAVY }}>Candidate Journey</h4>
        <div style={{ fontSize: '0.75rem', color: GREY_TXT }}>
          {is_terminal ? (
            <span data-testid="journey-terminal-badge" style={{ background: '#FEE2E2', color: '#DC2626', padding: '0.2rem 0.6rem', borderRadius: 999, fontWeight: 700, textTransform: 'capitalize' }}>{terminal_stage}</span>
          ) : (
            <span data-testid="journey-summary">Current: <strong style={{ color: NAVY, textTransform: 'capitalize' }}>{LABELS[current_stage] || current_stage}</strong>
              {currentStep && <> &middot; <strong>{fmtDays(currentStep.duration_days)}</strong> in stage</>}
            </span>
          )}
          <span> &middot; <strong>{fmtDays(total_days)}</strong> total as of {today}</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {stages.map((s, i) => {
          const color = stepColor(s.status);
          const reachedDate = s.reached_at ? new Date(s.reached_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
          return (
            <div key={s.stage} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {/* connector to previous */}
              {i > 0 && (
                <div style={{ position: 'absolute', top: 13, right: '50%', width: '100%', height: 3, background: stages[i - 1].status === 'done' || (stages[i - 1].status === 'current') ? NAVY : GREY, zIndex: 0 }} />
              )}
              <div data-testid={`journey-step-${s.stage}`} title={reachedDate ? `Reached ${reachedDate}` : 'Not reached yet'} style={{
                width: 28, height: 28, borderRadius: '50%', zIndex: 1,
                background: s.status === 'upcoming' ? '#fff' : color,
                border: `3px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: s.status === 'current' ? NAVY : '#fff', fontSize: '0.7rem', fontWeight: 700,
                boxShadow: s.status === 'current' ? `0 0 0 4px ${GOLD}33` : 'none',
              }}>
                {s.status === 'done' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (i + 1)}
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', fontWeight: 600, color: s.status === 'upcoming' ? GREY_TXT : NAVY, textAlign: 'center' }}>{LABELS[s.stage]}</div>
              <div style={{ fontSize: '0.625rem', color: GREY_TXT, textAlign: 'center', marginTop: '0.125rem', lineHeight: 1.4 }}>
                {reachedDate ? <div>{reachedDate}</div> : <div>&mdash;</div>}
                {s.duration_days !== null && s.duration_days !== undefined && (
                  <div style={{ color: s.status === 'current' ? '#B45309' : GREEN, fontWeight: 600 }}>{fmtDays(s.duration_days)}{s.status === 'current' ? ' so far' : ''}</div>
                )}
                {s.cumulative_days !== null && s.cumulative_days !== undefined && s.stage !== 'applied' && (
                  <div>{fmtDays(s.cumulative_days)} total</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Compact segmented bar for candidate list rows.
export function CandidateJourneyCompact({ journey }) {
  if (!journey || !journey.stages) return null;
  const { stages, is_terminal, terminal_stage, total_days, current_stage } = journey;
  return (
    <div data-testid="candidate-journey-compact" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 110 }}>
      <div style={{ display: 'flex', gap: 2 }}>
        {stages.map((s) => (
          <div key={s.stage} title={LABELS[s.stage]} style={{
            flex: 1, height: 6, borderRadius: 3,
            background: is_terminal && s.status !== 'done' ? GREY : stepColor(s.status),
          }} />
        ))}
      </div>
      <div style={{ fontSize: '0.625rem', color: GREY_TXT }}>
        {is_terminal ? (
          <span style={{ color: '#DC2626', fontWeight: 700, textTransform: 'capitalize' }}>{terminal_stage}</span>
        ) : (
          <span style={{ textTransform: 'capitalize', color: NAVY, fontWeight: 600 }}>{LABELS[current_stage] || current_stage}</span>
        )}
        <span> &middot; {fmtDays(total_days)} total</span>
      </div>
    </div>
  );
}
